import React, { FC } from 'react'

import { alpha, Box, useTheme } from '@mui/material'
import { every } from 'lodash'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { chartOptions } from '../chartOptions'
import { EmptyData } from '../EmptyData'
import { Indicators } from '../Indicators'
import { ReportWidgetsHeader } from '../ReportWidgetsHeader'
import { StyledWidgetWrapper } from '../StyledWidgetWrapper'
import { StressDistributionType } from '../types'

export const StressDistributionWidget: FC<{ stressDistribution: StressDistributionType[] }> = ({
  stressDistribution,
}) => {
  const { t } = useTranslation()
  const { palette } = useTheme()

  const chartData = {
    labels: stressDistribution?.map(({ time }) => time),
    datasets: [
      {
        label: `${t('positive', 'Positive')}`,
        data: stressDistribution?.map(({ positivePerCent }) => positivePerCent),
        backgroundColor: palette.green[500],
        barPercentage: 0.82,
        categoryPercentage: 0.78,
        borderRadius: 3,
        hoverBackgroundColor: alpha(palette.green[500] as string, 0.82),
      },
      {
        label: `${t('negative', 'Negative')}`,
        data: stressDistribution?.map(({ negativePerCent }) => negativePerCent),
        backgroundColor: palette.red[400],
        barPercentage: 0.82,
        categoryPercentage: 0.78,
        borderRadius: 3,
        hoverBackgroundColor: alpha(palette.red[400] as string, 0.8),
      },
    ],
  }

  const isNoData = every(stressDistribution, ({ totalTrades }) => !totalTrades)

  return (
    <StyledWidgetWrapper sx={{ display: 'flex', flexDirection: 'column' }}>
      <ReportWidgetsHeader
        text={t('tradingSentimentByTime', 'Trading sentiment by time')}
        explanation={t(
          'tradingSentimentByTimeExplanation',
          'Shows the share of positive and negative journal assessments across trading times.',
        )}
      >
        <Indicators
          positiveLabel={t('positive', 'Positive')}
          negativeLabel={t('negative', 'Negative')}
        />
      </ReportWidgetsHeader>
      {isNoData ? (
        <Box mt={4}>
          <EmptyData
            title={t('notEnoughData', 'Not enough data')}
            subtitle={t('pleaseCompleteJournalingSection')}
          />
        </Box>
      ) : (
        <Box width="100%" minWidth={0} flex={1} minHeight={220}>
          <Bar
            data={chartData}
            options={{
              ...chartOptions({ showPercentage: true, displayLegend: false }),
              responsive: true,
              maintainAspectRatio: false,
            }}
            style={{ height: '100%', width: '100%' }}
          />
        </Box>
      )}
    </StyledWidgetWrapper>
  )
}
